import React, { useState, useMemo } from 'react';
import { Card, Table, Button, Modal, Form, InputNumber, Tooltip, Badge, Space, Typography, Spin, Alert, Tag, Input } from 'antd';
import { ReloadOutlined, SettingOutlined, SearchOutlined, FileExcelOutlined, FileTextOutlined, BlockOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { useMonitoredAddresses, useReloadAddresses, useBloomFilterConfig, useUpdateBloomFilterConfig } from '../../hooks/useIndexerManagement';
import { formatDate } from '../../utils/formatters';
import ReprocessBlockModal from './ReprocessBlockModal';

const { Title, Text } = Typography;

interface IndexerAddressManagementProps {
  blockchain: string;
}

interface MonitoredAddress {
  address: string;
  userId?: string | number;
  addedAt?: string;
  key?: number;
  [key: string]: any; // For any additional properties
}

const IndexerAddressManagement: React.FC<IndexerAddressManagementProps> = ({ blockchain }) => {
  const { t } = useTranslation(['indexer', 'common']);
  const [isAddressModalVisible, setIsAddressModalVisible] = useState(false);
  const [isConfigModalVisible, setIsConfigModalVisible] = useState(false);
  const [isReprocessBlockModalVisible, setIsReprocessBlockModalVisible] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [configForm] = Form.useForm();

  // Fetch monitored addresses
  const {
    data: addressesData,
    isLoading: isLoadingAddresses,
    error: addressesError,
    refetch: refetchAddresses
  } = useMonitoredAddresses(blockchain, !!blockchain);

  // Fetch Bloom filter configuration
  const {
    data: configData,
    isLoading: isLoadingConfig,
    error: configError,
    refetch: refetchConfig
  } = useBloomFilterConfig();

  // Mutations
  const reloadAddressesMutation = useReloadAddresses();
  const updateConfigMutation = useUpdateBloomFilterConfig();

  // Handle address reload
  const handleReloadAddresses = () => {
    if (!blockchain) return;

    reloadAddressesMutation.mutate(blockchain, {
      onSuccess: () => {
        refetchAddresses();
      }
    });
  };

  // Export addresses to CSV
  const exportToCSV = () => {
    if (!addresses.length) return;

    // Create CSV content
    const headers = ['Address', 'User ID', 'Status', 'Added At'];
    const csvContent = [
      headers.join(','),
      ...addresses.map((addr: MonitoredAddress) =>
        `"${addr.address}","${addr.userId || ''}","active","${addr.addedAt || ''}"`
      )
    ].join('\n');

    // Create and download the file
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `${blockchain}-addresses-${new Date().toISOString().split('T')[0]}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Export addresses to JSON
  const exportToJSON = () => {
    if (!addresses.length) return;

    // Create JSON content
    const jsonContent = JSON.stringify(addresses, null, 2);

    // Create and download the file
    const blob = new Blob([jsonContent], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `${blockchain}-addresses-${new Date().toISOString().split('T')[0]}.json`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Handle config update
  const handleUpdateConfig = (values: any) => {
    updateConfigMutation.mutate({
      bloomFilterThreshold: values.bloomFilterThreshold,
      bloomFilterRebuildThreshold: values.bloomFilterRebuildThreshold
    }, {
      onSuccess: () => {
        setIsConfigModalVisible(false);
        refetchConfig();
      }
    });
  };

  // Show addresses modal
  const showAddressesModal = () => {
    refetchAddresses();
    setIsAddressModalVisible(true);
  };

  // Show config modal
  const showConfigModal = () => {
    if (configData?.config) {
      configForm.setFieldsValue({
        bloomFilterThreshold: configData.config.bloomFilterThreshold,
        bloomFilterRebuildThreshold: configData.config.bloomFilterRebuildThreshold
      });
    }
    setIsConfigModalVisible(true);
  };

  // Show reprocess block modal
  const showReprocessBlockModal = () => {
    setIsReprocessBlockModalVisible(true);
  };

  // Get addresses from data
  const addresses = useMemo(() => {
    if (!addressesData || !Array.isArray(addressesData.addresses)) return [];

    return addressesData.addresses.map((addr: any) => {
      // Handle both string addresses and object addresses
      if (typeof addr === 'string') {
        return { address: addr };
      } else if (typeof addr === 'object' && addr !== null) {
        // Ensure the address object has an address property
        return { ...addr, address: addr.address || JSON.stringify(addr) };
      }
      return { address: String(addr) };
    });
  }, [addressesData]);

  const addressCount = addresses.length;

  // Filter addresses based on search text
  const filteredAddresses = useMemo(() => {
    if (!searchText) return addresses;

    return addresses.filter((addr: MonitoredAddress) =>
      (addr.address || '').toLowerCase().includes(searchText.toLowerCase()) ||
      (addr.userId && String(addr.userId).toLowerCase().includes(searchText.toLowerCase()))
    );
  }, [addresses, searchText]);

  // Get config from data
  const config = configData?.config || {
    bloomFilterThreshold: 50,
    bloomFilterRebuildThreshold: 100,
    addressReloadEnabled: true,
    addressReloadInterval: 300000,
    metrics: {
      addressReloads: 0,
      newAddressesFound: 0,
      bloomFilterRebuilds: 0,
      lastReloadTime: null,
      reloadErrors: 0
    }
  };

  // Determine if Bloom filter is active
  const isBloomFilterActive = addressCount >= config.bloomFilterThreshold;

  // Address table columns
  const addressColumns = [
    {
      title: t('indexer:address'),
      dataIndex: 'address',
      key: 'address',
      render: (text: string) => (
        <Typography.Text copyable={{ text }} ellipsis style={{ maxWidth: 200 }}>
          {text}
        </Typography.Text>
      )
    },
    {
      title: t('indexer:userId'),
      dataIndex: 'userId',
      key: 'userId',
      render: (userId: any) => {
        if (userId === undefined || userId === null) {
          return '-';
        }
        return String(userId);
      }
    },
    {
      title: t('indexer:status'),
      dataIndex: 'status',
      key: 'status',
      render: (status: string) => {
        // For monitored addresses, we assume they are active by default
        // since they are being tracked by the indexer
        const addressStatus = status || 'active';

        switch (addressStatus.toLowerCase()) {
          case 'active':
            return <Badge status="success" text={t('indexer:active')} />;
          case 'inactive':
            return <Badge status="error" text={t('indexer:inactive')} />;
          case 'pending':
            return <Badge status="processing" text={t('indexer:pending')} />;
          default:
            return <Badge status="default" text={t('indexer:unknown')} />;
        }
      }
    },
    {
      title: t('indexer:addedAt'),
      dataIndex: 'addedAt',
      key: 'addedAt',
      render: (date: string) => {
        if (!date) return '-';
        return formatDate(date);
      }
    }
  ];

  return (
    <Card
      title={
        <Space>
          <Title level={5}>{t('indexer:addressManagement')}</Title>
          <Tag color={isBloomFilterActive ? 'blue' : 'default'}>
            {isBloomFilterActive ? t('indexer:bloomFilterActive') : t('indexer:bloomFilterInactive')}
          </Tag>
        </Space>
      }
      extra={
        <Space>
          <Tooltip title={t('indexer:reloadAddresses')}>
            <Button
              icon={<ReloadOutlined />}
              onClick={handleReloadAddresses}
              loading={reloadAddressesMutation.isPending}
            />
          </Tooltip>
          <Tooltip title={t('indexer:configureBloomFilter')}>
            <Button
              icon={<SettingOutlined />}
              onClick={showConfigModal}
            />
          </Tooltip>
          <Tooltip title={t('indexer:reprocessBlock')}>
            <Button
              icon={<BlockOutlined />}
              onClick={showReprocessBlockModal}
            />
          </Tooltip>
        </Space>
      }
    >
      {/* Address Count and Bloom Filter Status */}
      <Space direction="vertical" style={{ width: '100%', marginBottom: 16 }}>
        <Space>
          <Text strong>{t('indexer:monitoredAddresses')}:</Text>
          <Badge
            count={addressCount}
            showZero
            style={{ backgroundColor: addressCount > 0 ? '#52c41a' : '#d9d9d9' }}
          />
          <Button type="link" onClick={showAddressesModal}>
            {t('indexer:viewAddresses')}
          </Button>
        </Space>

        <Space>
          <Text strong>{t('indexer:lastReload')}:</Text>
          <Text>
            {config.metrics?.lastReloadTime
              ? formatDate(new Date(config.metrics.lastReloadTime))
              : t('common:never')}
          </Text>
        </Space>

        <Space>
          <Text strong>{t('indexer:reloadStatus')}:</Text>
          <Text>
            {config.addressReloadEnabled
              ? t('indexer:enabled')
              : t('indexer:disabled')}
            {' '}
            ({Math.round(config.addressReloadInterval / 1000)} {t('common:seconds')})
          </Text>
        </Space>
      </Space>

      {/* Metrics */}
      <Card size="small" title={t('indexer:metrics')} style={{ marginBottom: 16 }}>
        <Space direction="vertical" style={{ width: '100%' }}>
          <Space>
            <Text strong>{t('indexer:totalReloads')}:</Text>
            <Text>{config.metrics?.addressReloads || 0}</Text>
          </Space>

          <Space>
            <Text strong>{t('indexer:newAddressesFound')}:</Text>
            <Text>{config.metrics?.newAddressesFound || 0}</Text>
          </Space>

          <Space>
            <Text strong>{t('indexer:bloomFilterRebuilds')}:</Text>
            <Text>{config.metrics?.bloomFilterRebuilds || 0}</Text>
          </Space>

          <Space>
            <Text strong>{t('indexer:reloadErrors')}:</Text>
            <Text>{config.metrics?.reloadErrors || 0}</Text>
          </Space>
        </Space>
      </Card>

      {/* Addresses Modal */}
      <Modal
        title={`${t('indexer:monitoredAddresses')} - ${blockchain?.toUpperCase()}`}
        open={isAddressModalVisible}
        onCancel={() => setIsAddressModalVisible(false)}
        footer={[
          <Button key="reload" type="primary" onClick={handleReloadAddresses} loading={reloadAddressesMutation.isPending}>
            {t('indexer:reloadAddresses')}
          </Button>,
          <Button key="close" onClick={() => setIsAddressModalVisible(false)}>
            {t('common:close')}
          </Button>
        ]}
        width={800}
      >
        {isLoadingAddresses ? (
          <div style={{ textAlign: 'center', padding: '20px' }}>
            <Spin />
            <div style={{ marginTop: '10px' }}>{t('common:loading')}</div>
          </div>
        ) : addressesError ? (
          <Alert
            message={t('common:error')}
            description={String(addressesError)}
            type="error"
            showIcon
          />
        ) : (
          <>
            <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between' }}>
              <Input
                placeholder={t('indexer:searchAddresses')}
                prefix={<SearchOutlined />}
                value={searchText}
                onChange={e => setSearchText(e.target.value)}
                style={{ width: 300 }}
                allowClear
              />
              <Space>
                <Tooltip title={t('indexer:exportToCSV')}>
                  <Button
                    icon={<FileExcelOutlined />}
                    onClick={exportToCSV}
                    disabled={!addresses.length}
                  >
                    {t('indexer:exportCSV')}
                  </Button>
                </Tooltip>
                <Tooltip title={t('indexer:exportToJSON')}>
                  <Button
                    icon={<FileTextOutlined />}
                    onClick={exportToJSON}
                    disabled={!addresses.length}
                  >
                    {t('indexer:exportJSON')}
                  </Button>
                </Tooltip>
              </Space>
            </div>

            <div style={{ marginBottom: 16 }}>
              <Text>
                {t('indexer:showing')} {filteredAddresses.length} {t('indexer:of')} {addresses.length} {t('indexer:addresses')}
              </Text>
            </div>

            <Table
              dataSource={filteredAddresses.map((addr: MonitoredAddress, index: number) => ({ ...addr, key: index }))}
              columns={addressColumns}
              pagination={{
                pageSize: 10,
                showSizeChanger: true,
                pageSizeOptions: ['10', '20', '50', '100'],
                showTotal: (total, range) => `${range[0]}-${range[1]} ${t('indexer:of')} ${total} ${t('indexer:items')}`
              }}
              size="small"
            />
          </>
        )}
      </Modal>

      {/* Bloom Filter Configuration Modal */}
      <Modal
        title={t('indexer:bloomFilterConfiguration')}
        open={isConfigModalVisible}
        onCancel={() => setIsConfigModalVisible(false)}
        footer={null}
      >
        {isLoadingConfig ? (
          <div style={{ textAlign: 'center', padding: '20px' }}>
            <Spin />
            <div style={{ marginTop: '10px' }}>{t('common:loading')}</div>
          </div>
        ) : configError ? (
          <Alert
            message={t('common:error')}
            description={String(configError)}
            type="error"
            showIcon
          />
        ) : (
          <Form
            form={configForm}
            layout="vertical"
            initialValues={{
              bloomFilterThreshold: config.bloomFilterThreshold,
              bloomFilterRebuildThreshold: config.bloomFilterRebuildThreshold
            }}
            onFinish={handleUpdateConfig}
          >
            <Form.Item
              name="bloomFilterThreshold"
              label={t('indexer:bloomFilterThreshold')}
              tooltip={t('indexer:bloomFilterThresholdTooltip')}
              rules={[{ required: true, message: t('common:required') }]}
            >
              <InputNumber min={1} style={{ width: '100%' }} />
            </Form.Item>

            <Form.Item
              name="bloomFilterRebuildThreshold"
              label={t('indexer:bloomFilterRebuildThreshold')}
              tooltip={t('indexer:bloomFilterRebuildThresholdTooltip')}
              rules={[{ required: true, message: t('common:required') }]}
            >
              <InputNumber min={1} style={{ width: '100%' }} />
            </Form.Item>

            <Form.Item>
              <Space style={{ width: '100%', justifyContent: 'flex-end' }}>
                <Button onClick={() => setIsConfigModalVisible(false)}>
                  {t('common:cancel')}
                </Button>
                <Button type="primary" htmlType="submit" loading={updateConfigMutation.isPending}>
                  {t('common:save')}
                </Button>
              </Space>
            </Form.Item>
          </Form>
        )}
      </Modal>

      {/* Reprocess Block Modal */}
      <ReprocessBlockModal
        blockchain={blockchain}
        visible={isReprocessBlockModalVisible}
        onClose={() => setIsReprocessBlockModalVisible(false)}
      />
    </Card>
  );
};

export default IndexerAddressManagement;
